/* eslint-disable react/jsx-props-no-spreading */
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  CircularProgress,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { isNil } from 'lodash-es';
import React, { SyntheticEvent, useMemo } from 'react';
import { useController } from 'react-hook-form';

export interface AutocompleteTextFieldProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> extends Omit<AutocompleteProps<T | string, Multiple, DisableClearable, FreeSolo>, 'renderInput'> {
  name: string;
  label: string;
  options: any[];
  inputProps?: TextFieldProps;
  // Fail-safe label if we can't find the preselected value in the options
  // This may happen if the options is paginated.
  valueLabel?: string;
  rules?: any;
  placeholder?: string;
  onInputChange?: (event: SyntheticEvent<Element, Event>, value: string, reason: AutocompleteInputChangeReason) => void;
}

function AutocompleteTextField<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined
>({
  name,
  label,
  options,
  inputProps = null,
  valueLabel = '',
  loading,
  rules,
  placeholder,
  ...rest
}: AutocompleteTextFieldProps<T, Multiple, DisableClearable, FreeSolo>) {
  const { field, fieldState } = useController({ name, rules });

  const enhancedOptions = useMemo(() => {
    if (!field.value || !options.length) return options;
    
    const valueExists = options.some(option => 
      String(option.value) === String(field.value)
    );
    
    if (valueExists) return options;
    
    const fallbackOption = {
      label: valueLabel || String(field.value),
      value: field.value
    };
    
    return [fallbackOption, ...options];
  }, [options, field.value, valueLabel]);

  return (
    <Autocomplete
      options={enhancedOptions}
      size="small"
      value={field.value ?? null}
      onChange={(_, item) => field.onChange(isNil(item?.value) ? '' : item?.value)}
      isOptionEqualToValue={(option, value) => {
        if (!option || value === null || value === undefined) return false;
        return String(option.value) === String(value);
      }}
      filterOptions={(x) => x}
      loading={loading}
      renderInput={(params: AutocompleteRenderInputParams): React.ReactNode => (
        <TextField
          variant="standard"
          size="small"
          label={label}
          name={field.name}
          {...inputProps}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          placeholder={placeholder || `Select ${label || 'Value'}`}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
          {...params}
        />
      )}
      // getOptionLabel is used to render the selected value.
      // If a value is preselected in the form, it will be a number string.
      // If not preselected, it will be an option object with a value and label.
      getOptionLabel={(value) =>
        !Number.isNaN(Number(value))
          ? enhancedOptions.find((option) => String(option.value) === String(value))?.label || valueLabel || String(value)
          : value.label || field.value
      }
      // renderOption must be defined or else getOptionLabel will be used as default, as per documentation.
      renderOption={(props, option) => (
        <li {...props}>
          <Typography>{option.label || option.name}</Typography>
        </li>
      )}
      {...rest}
    />
  );
}

export default AutocompleteTextField;